import type { CountryCode } from 'libphonenumber-js/max';
import { parsePhoneNumberFromString } from 'libphonenumber-js/max';

const DEFAULT_COUNTRY: CountryCode = 'DE';

/**
 * Digits, plus, space, parentheses, period, hyphen. Everything a phone number
 * is ever written with, nothing else.
 */
const FORBIDDEN_CHARACTERS = /[^\d+ ().-]/g;

const DIGITS = /\D/g;

/**
 * Hard safety net, counted on the E.164 form. Outside this, submission is
 * blocked outright - nothing plausibly phone-shaped is this short or long.
 * Kept deliberately wide so a real (if unusual) number never gets silently
 * rejected here.
 */
const MIN_DIGITS = 5;
const MAX_DIGITS = 18;

/**
 * The range a typical real number falls into. Outside this but still within
 * MIN_DIGITS..MAX_DIGITS, the number isn't blocked - it's asked about instead.
 * libphonenumber's own metadata calls some very short numbers "valid" (e.g.
 * Berlin's "030" with a 3-digit subscriber, a legacy allowance), which is
 * technically true but not a number anyone is likely to actually type here.
 */
const SUSPICIOUS_MIN_DIGITS = 11;
const SUSPICIOUS_MAX_DIGITS = 15;

/**
 * Numbers that look made up. Tested against the value as typed, so separators
 * are visible to the pattern. Add to the list as new junk shows up.
 */
const INVALID_PATTERNS: RegExp[] = [
  // One digit repeated all the way: 0000000, 11111111111, 000 0000.
  /^\+?[\s().-]*(\d)(?:[\s().-]*\1)+[\s().-]*$/,
  // Two hyphens in a row.
  /--/,
];

/**
 * A run this long, of either shape, essentially never occurs in a real
 * assigned subscriber number - only in typed-to-get-past-the-field junk. Runs
 * anywhere in the E.164 digits, not just for the whole string, so "0170
 * 0000000" and "030 12345678" (real prefix, fake tail) are caught too, not
 * only numbers that are fake from the very first digit.
 */
const MIN_MADE_UP_RUN = 6;

/**
 * True if `digits` contains `run` or more consecutive characters that are all
 * identical: "491700000000" -> true (six 0s), "4917012345" -> false.
 */
const hasLongRepeatRun = (digits: string, run: number): boolean => {
  let streak = 1;
  for (let i = 1; i < digits.length; i++) {
    streak = digits[i] === digits[i - 1] ? streak + 1 : 1;
    if (streak >= run) return true;
  }
  return false;
};

/**
 * True if `digits` contains `run` or more consecutive digits that count
 * straight up or straight down: "491701234567" -> true (1234567 ascends by
 * one each step), "491234509876" -> also true (descends).
 */
const hasLongSequentialRun = (digits: string, run: number): boolean => {
  let ascending = 1;
  let descending = 1;
  for (let i = 1; i < digits.length; i++) {
    const step = Number(digits[i]) - Number(digits[i - 1]);
    ascending = step === 1 ? ascending + 1 : 1;
    descending = step === -1 ? descending + 1 : 1;
    if (ascending >= run || descending >= run) return true;
  }
  return false;
};

const looksMadeUp = (digits: string): boolean =>
  hasLongRepeatRun(digits, MIN_MADE_UP_RUN) || hasLongSequentialRun(digits, MIN_MADE_UP_RUN);

/**
 * A German number typed in E.164 without the plus ends up with the country
 * code twice: "491701234567" parses to +49 49 1701234567.
 */
const DOUBLED_GERMAN_CODE = '+4949';

/**
 * From this many digits on, a leading 49 without a plus cannot be anything but
 * the country code, so the missing plus is added silently instead of asking.
 */
const GERMAN_E164_MIN_DIGITS = 12;

export type PhoneError = 'too-short' | 'too-long';

export type PhoneSuspicion =
  | 'too-short'
  | 'too-long'
  | 'invalid-pattern'
  | 'unknown-country'
  | 'unrecognised'
  | 'doubled-country-code';

export interface PhoneVerdict {
  error: PhoneError | null;
  suspicion: PhoneSuspicion | null;
  /**
   * What the confirmation dialog should offer: E.164 where we could read the
   * number, with the doubled country code dropped. Falls back to the input.
   */
  value: string;
}

const clean: PhoneVerdict = { error: null, suspicion: null, value: '' };

const digitCount = (value: string): number => value.replace(DIGITS, '').length;

const withoutDoubledCode = (number: string): string => `+49${number.slice(DOUBLED_GERMAN_CODE.length)}`;

const isGermanE164WithoutPlus = (input: string, digits: string): boolean =>
  !input.startsWith('+') && digits.startsWith('49') && digits.length >= GERMAN_E164_MIN_DIGITS;

/**
 * Legacy regex-based E.164 coercion - this module's predecessor (`parsePhoneNumber` in
 * utils.js, removed 2026-09-02). Used only as a fallback for input libphonenumber cannot
 * parse as any recognised number ('unrecognised', below). Unlike the rest of this module it
 * never gives up: it always forces *something* shaped like +49XXXXXXXXXXXXX out of whatever
 * was typed. That is worse for correctness (a nonsense number can end up looking plausible)
 * but safer for downstream partners whose own validation only accepts that shape - sending
 * them raw, unnormalised text for numbers libphonenumber can't read caused otherwise-good
 * leads to be silently rejected further down the pipeline (BGI Gravity Forms, 2026-09-03).
 */
const legacyE164Fallback = (input: string, code = '49'): string => {
  const MAX_WITHOUT_COUNTRY_CODE = 15;
  const COUNTRY_CODE_LENGTH = 2;
  const maxLength = 1 + COUNTRY_CODE_LENGTH + MAX_WITHOUT_COUNTRY_CODE;

  if (!input) return '';

  if (input.startsWith('+')) {
    const cleared = `+${input.slice(1).replace(DIGITS, '')}`;
    return cleared.slice(0, maxLength);
  }

  const cleared = input.replace(DIGITS, '');
  if (!cleared) return `+${code}000000000000000`;
  if (cleared.startsWith('00')) return cleared.replace(/^.{2}/, '+').slice(0, maxLength);
  if (cleared.startsWith('0')) return cleared.replace(/^.{1}/, `+${code}`).slice(0, maxLength);
  return `+${code}${cleared}`.slice(0, maxLength);
};

/**
 * Single pass over one value. Memoised on the last input because the schema
 * asks the same question once per rule on every keystroke.
 */
let last: { input: string; verdict: PhoneVerdict } | null = null;

function evaluate(input: string): PhoneVerdict {
  if (!input) return clean;

  const german = parsePhoneNumberFromString(input, DEFAULT_COUNTRY);
  const doubled = german?.number.startsWith(DOUBLED_GERMAN_CODE) ?? false;

  // The doubled code is dropped before anything is measured: it inflates the
  // length by two digits, so judging length first would blame the wrong rule
  // and hand the dialog the uncorrected number.
  const e164 = german ? (doubled ? withoutDoubledCode(german.number) : german.number) : input;

  const digits = digitCount(e164);
  if (digits < MIN_DIGITS) return { error: 'too-short', suspicion: null, value: e164 };
  if (digits > MAX_DIGITS) return { error: 'too-long', suspicion: null, value: e164 };
  if (digits < SUSPICIOUS_MIN_DIGITS) return { error: null, suspicion: 'too-short', value: e164 };
  if (digits > SUSPICIOUS_MAX_DIGITS) return { error: null, suspicion: 'too-long', value: e164 };

  // First among the remaining suspicions: a made up number still parses, so
  // the branches below would hand it back as clean. Checked against both
  // what was typed (catches "--" and a value that's one digit for its entire
  // length) and the normalised E.164 digits (catches a real-looking prefix
  // glued to a fake uniform or sequential tail, in whatever format it was
  // typed).
  if (INVALID_PATTERNS.some((pattern) => pattern.test(input)) || looksMadeUp(e164.replace(DIGITS, ''))) {
    return { error: null, suspicion: 'invalid-pattern', value: e164 };
  }

  // No default country on purpose: whatever survives this is a number the user
  // wrote in full international form, and libphonenumber names its country.
  const international = parsePhoneNumberFromString(input);
  if (international) {
    const suspicion = international.country ? null : 'unknown-country';
    return { error: null, suspicion, value: international.number };
  }

  if (!german) return { error: null, suspicion: 'unrecognised', value: legacyE164Fallback(input) };
  if (doubled && !isGermanE164WithoutPlus(input, input.replace(DIGITS, ''))) {
    return { error: null, suspicion: 'doubled-country-code', value: e164 };
  }
  return { error: null, suspicion: null, value: e164 };
}

export default class Phone {
  /**
   * Drops characters that cannot belong to a phone number. Used as the input
   * filter, so it must never reorder or add anything - only remove.
   */
  public static sanitize(value: string): string {
    return value.replace(FORBIDDEN_CHARACTERS, '');
  }

  public static verdict(value: string): PhoneVerdict {
    if (last?.input === value) return last.verdict;
    const verdict = evaluate(value);
    last = { input: value, verdict };
    return verdict;
  }

  public static error(value: string) {
    return Phone.verdict(value).error;
  }

  public static suspicion(value: string) {
    return Phone.verdict(value).suspicion;
  }

  /**
   * Value to prefill the confirmation dialog with.
   */
  public static suggest(value: string) {
    return Phone.verdict(value).value;
  }

  /**
   * What goes to the backend. Same string the dialog would have proposed, so a
   * number corrected without asking cannot reach success.php uncorrected.
   */
  public static toE164(value: string): string {
    return Phone.verdict(value).value;
  }
}
